import { ServiceClient } from 'src/utils'
import type { Periodo } from './Dashboards.periodo'
import type {
    Consulta,
    Consultas,
    FilhosParceiro,
    FiltrosConsultas,
    MensagemDlq,
    OpcoesConsultas,
    ParceiroEncontrado,
    Negocios,
    Operacao,
    OrdemParceiros,
    Origem,
    Parceiro,
    Parceiros,
    ReservaVencendo,
    SituacaoParceiro
} from './Dashboards.scopes'

const serviceClient = ServiceClient.singleton()

const BASE_URL = '/api/dashboard'

export class DashboardsService {
    static #INSTANCE = new DashboardsService()
    static readonly singleton = () => DashboardsService.#INSTANCE

    async operacao(periodo: Periodo): Promise<Operacao> {
        const params = new URLSearchParams(periodo)
        return serviceClient.authGet<Operacao>(`${BASE_URL}/operacao?${params.toString()}`)
    }

    async dlq(
        periodo: Periodo,
        canal: string,
        offset: number,
        limit: number
    ): Promise<{ total: number; itens: MensagemDlq[] }> {
        const params = new URLSearchParams({ ...periodo, offset: `${offset}`, limit: `${limit}` })
        if (canal) params.set('canal', canal)
        return serviceClient.authGet(`${BASE_URL}/operacao/dlq?${params.toString()}`)
    }

    async negocios(periodo: Periodo, origem: Origem): Promise<Negocios> {
        const params = new URLSearchParams({ ...periodo, origem })
        return serviceClient.authGet<Negocios>(`${BASE_URL}/negocios?${params.toString()}`)
    }

    async parceiros(periodo: Periodo): Promise<Parceiros> {
        const params = new URLSearchParams(periodo)
        return serviceClient.authGet<Parceiros>(`${BASE_URL}/parceiros?${params.toString()}`)
    }

    async rankingParceiros(
        periodo: Periodo,
        busca: string,
        situacao: SituacaoParceiro,
        ordem: OrdemParceiros,
        offset: number,
        limit: number
    ): Promise<{ total: number; itens: Parceiro[] }> {
        const params = new URLSearchParams({ ...periodo, situacao, ordem, offset: `${offset}`, limit: `${limit}` })
        if (busca) params.set('q', busca)
        return serviceClient.authGet(`${BASE_URL}/parceiros/ranking?${params.toString()}`)
    }

    // :: Consultas de lead (POST: os filtros podem levar listas coladas de CNPJs e negócios)

    async consultas(periodo: Periodo, filtros: FiltrosConsultas): Promise<Consultas> {
        return serviceClient.authPost<Consultas>(`${BASE_URL}/consultas`, { ...periodo, filtros })
    }

    async filhosParceiro(periodo: Periodo, filtros: FiltrosConsultas, caminho: string[]): Promise<FilhosParceiro> {
        return serviceClient.authPost<FilhosParceiro>(`${BASE_URL}/consultas/parceiros`, {
            ...periodo,
            filtros,
            caminho
        })
    }

    async detalheConsultas(
        periodo: Periodo,
        filtros: FiltrosConsultas,
        offset: number,
        limit: number
    ): Promise<{ total: number; itens: Consulta[] }> {
        return serviceClient.authPost(`${BASE_URL}/consultas/detalhe`, { ...periodo, filtros, offset, limit })
    }

    async opcoesConsultas(periodo: Periodo): Promise<OpcoesConsultas> {
        const params = new URLSearchParams(periodo)
        return serviceClient.authGet<OpcoesConsultas>(`${BASE_URL}/consultas/opcoes?${params.toString()}`)
    }

    async buscarParceiros(busca: string): Promise<ParceiroEncontrado[]> {
        const params = new URLSearchParams({ q: busca })
        return serviceClient.authGet<ParceiroEncontrado[]>(`${BASE_URL}/consultas/parceiros/busca?${params.toString()}`)
    }

    async reservasVencendo(
        origem: Origem,
        dias: 7 | 15,
        offset: number,
        limit: number
    ): Promise<{ total: number; itens: ReservaVencendo[] }> {
        const params = new URLSearchParams({ origem, dias: `${dias}`, offset: `${offset}`, limit: `${limit}` })
        return serviceClient.authGet(`${BASE_URL}/negocios/vencendo?${params.toString()}`)
    }
}

export default DashboardsService.singleton()
